import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Endpoint Formspree DFPRO Lyon (remplace le handler Readdy qui ne survit pas à l'export)
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mlgqoaqn';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot : si rempli => bot. On simule un succès et on redirige sans envoyer.
    const hpField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (hpField && hpField.value.trim() !== '') {
      navigate('/merci/');
      return;
    }

    setStatus('loading');
    setStatusMessage('');

    try {
      const formData = new FormData(form);
      if (hpField) formData.delete('phone_alt');
      // Champ source = page d'origine du lead + sujet lisible dans l'email
      const source = typeof window !== 'undefined' ? window.location.pathname : '';
      formData.append('source', source);
      formData.append('_subject', `Nouveau contact DFPRO Lyon — ${source}`);

      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (response.ok) {
        form.reset();
        navigate('/merci/');
      } else {
        let msg = "Une erreur est survenue lors de l'envoi. Veuillez réessayer ou nous appeler au 09 80 80 36 84.";
        try {
          const data = await response.json();
          if (data?.errors?.length) msg = data.errors.map((x: { message: string }) => x.message).join(' ');
        } catch {
          // garde le message par défaut
        }
        setStatus('error');
        setStatusMessage(msg);
      }
    } catch {
      setStatus('error');
      setStatusMessage('Erreur réseau. Vérifiez votre connexion, réessayez ou appelez le 09 80 80 36 84.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {status === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700 flex items-center gap-2">
            <i className="ri-error-warning-fill text-red-500 w-5 h-5 flex items-center justify-center"></i>
            {statusMessage}
          </p>
        </div>
      )}

      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
          Nom complet <span className="text-accent-500">*</span>
        </label>
        <input type="text" id="contact-name" name="name" required
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-secondary-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
          placeholder="Votre nom et prénom" />
      </div>

      <div>
        <label htmlFor="contact-phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
          Téléphone <span className="text-accent-500">*</span>
        </label>
        <input type="tel" id="contact-phone" name="phone" required
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-secondary-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
          placeholder="06 12 34 56 78" />
      </div>

      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">Email</label>
        <input type="email" id="contact-email" name="email"
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-secondary-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
          placeholder="votre@email.fr" />
      </div>

      <div>
        <label htmlFor="contact-city" className="block text-sm font-medium text-foreground-700 mb-1.5">Ville</label>
        <input type="text" id="contact-city" name="city"
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-secondary-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
          placeholder="Lyon, Villeurbanne…" />
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          Votre demande <span className="text-accent-500">*</span>
        </label>
        <textarea id="contact-message" name="message" required maxLength={500} rows={4}
          className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-secondary-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
          placeholder="Décrivez votre panne ou votre besoin (volet bloqué, fenêtre cassée, devis…)"></textarea>
        <p className="text-xs text-secondary-400 mt-1">500 caractères maximum</p>
      </div>

      {/* Honeypot anti-spam (ne pas remplir) */}
      <input type="text" name="phone_alt" className="contact-hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }} />

      <button type="submit" disabled={status === 'loading'}
        className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 disabled:bg-accent-400 text-background-50 font-semibold text-sm px-6 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer">
        {status === 'loading' ? (
          <><i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>Envoi en cours…</>
        ) : (
          <><i className="ri-send-plane-fill w-4 h-4 flex items-center justify-center"></i>Envoyer ma demande</>
        )}
      </button>
    </form>
  );
}
